import { useMemo } from 'react';

import { useBoothInfo } from '@auth-v2';
import { useAtomValue } from 'jotai';

import { VideoPayload } from '@blocks';
import { useTranslate } from '@i18n';
import { useFloorSettings, useCameraSetup } from '@queries';
import { currentActivityIdAtom } from '@store';

const getLiveUrl = (beastEndpoint: string, cameraName: string, lockFrames = false) => {
    const normalized = /^https?:\/\//i.test(beastEndpoint) ? beastEndpoint : `https://${beastEndpoint}`;
    const url = new URL(normalized);
    url.pathname = '/api/v1/live-videos/' + cameraName;
    if (lockFrames) url.searchParams.set('subsample', '2');
    return url.toString();
};

const LIVE_VIEW_DEFAULT_VIDEOS = ['face_on', 'down_the_line'];

export const useVideoPayload = () => {
    const { data: floorSettings } = useFloorSettings();
    const activityId = useAtomValue(currentActivityIdAtom);
    const { data: cameraConfig } = useCameraSetup(activityId);
    const { data: boothInfo } = useBoothInfo();

    const t = useTranslate();
    return useMemo(() => {
        if (!boothInfo?.beastEndpoint || !cameraConfig?.cameras) {
            return [];
        }

        const defaultVideos = cameraConfig.cameras.filter((video) => LIVE_VIEW_DEFAULT_VIDEOS.includes(video.name));
        if (!floorSettings) {
            return defaultVideos.map<VideoPayload>((camera) => ({
                url: getLiveUrl(boothInfo?.beastEndpoint, camera.name, true),
                name: t(`camera_angle.${camera.name}`),
                type: 'live',
                id: camera.id,
                view: camera.view,
            }));
        }

        if (floorSettings.cameraAngle) {
            const selectedVideo = cameraConfig.cameras.filter((video) => video.name === floorSettings.cameraAngle);
            return (selectedVideo.length ? selectedVideo : defaultVideos).map<VideoPayload>((camera) => ({
                url: getLiveUrl(boothInfo?.beastEndpoint, camera.name),
                name: t(`camera_angle.${camera.name}`),
                type: 'live',
                id: camera.id,
                view: camera.view,
            }));
        }

        return defaultVideos.map<VideoPayload>((camera) => ({
            url: getLiveUrl(boothInfo?.beastEndpoint, camera.name, true),
            name: t(`camera_angle.${camera.name}`),
            type: 'live',
            id: camera.id,
            view: camera.view,
        }));
    }, [boothInfo?.beastEndpoint, cameraConfig?.cameras, floorSettings, t]);
};
